import { Tag, useFormatDate, useFormatDateTime } from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useContactAttributeOptions } from "../shared/hooks/useContactAttributeOptions";
import {
	ContactAttributeOption,
	ContactRelationColumn,
	IContactFilterItemResource,
	contactRelationEntityTypeToColumns,
} from "../shared/types";
import { mapFilterItemValueToString } from "./mappers";

const getFilterItemName = (
	option: ContactAttributeOption,
	item: IContactFilterItemResource,
	t: (relationColumn: ContactRelationColumn) => string,
) => {
	const attribute = option.attribute;
	const name = option.name;

	if (
		attribute.type === "relation" &&
		item.relationColumn &&
		contactRelationEntityTypeToColumns[attribute.entityType].length > 1
	) {
		return `${name} ${t(item.relationColumn)}`;
	} else {
		return name;
	}
};

interface Props {
	item: IContactFilterItemResource;
	onRemove: () => void;
	onClick: () => void;
}

export const ContactFilterBarItem: FC<Props> = ({ item, onRemove, onClick }) => {
	const { t } = useTranslation();
	const { contactAttributeOptions } = useContactAttributeOptions();
	const formatDate = useFormatDate();
	const formatDateTime = useFormatDateTime();

	const attributeOption = useMemo(
		() => contactAttributeOptions.find(({ id }) => id === item.contactAttribute.id),
		[contactAttributeOptions, item],
	);
	const filterItemValuesAsString = useMemo(() => {
		if (!attributeOption) return "";

		return mapFilterItemValueToString(attributeOption, item, { formatDate, formatDateTime, t });
	}, [attributeOption, item, formatDate, formatDateTime, t]);

	if (!attributeOption) {
		return (
			<Tag color="dark" onClose={onRemove} onClick={onClick}>
				Unknown attribute {item.contactAttribute.id}
			</Tag>
		);
	}

	const filterItemName = getFilterItemName(attributeOption, item, t);
	if (item.operator === "IS_EMPTY" || item.operator === "IS_NOT_EMPTY") {
		return (
			<Tag color="dark" onClose={onRemove} onClick={onClick}>
				{filterItemName} {t(item.operator)}
			</Tag>
		);
	}

	return (
		<Tag color="dark" onClose={onRemove} onClick={onClick}>
			{filterItemName} {t(item.operator)} {filterItemValuesAsString}
		</Tag>
	);
};
